class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.flickity = null;
    this._isMobile = null;
    this._resizeTimer = null;
    this._onMatchSmall = this.handleBreakpoint.bind(this);
    this._onUnmatchSmall = this.handleBreakpoint.bind(this);
    this._onResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.updateViewportMode();
    this.resetInlineHeights();

    window.addEventListener("resize", this._onResize);
    document.addEventListener("matchSmall", this._onMatchSmall);
    document.addEventListener("unmatchSmall", this._onUnmatchSmall);

    if (Number(this.dataset.blockCount) > 1) {
      this.initSlider();
    }
  }

  disconnectedCallback() {
    document.removeEventListener("matchSmall", this._onMatchSmall);
    document.removeEventListener("unmatchSmall", this._onUnmatchSmall);
    window.removeEventListener("resize", this._onResize);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this.unload();
  }

  updateViewportMode() {
    this._isMobile = window.matchMedia("(max-width: 749px)").matches;
    this.setAttribute("data-viewport", this._isMobile ? "mobile" : "desktop");
  }

  handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      if (this._isMobile === isMobile) {
        this.syncHeight();
        return;
      }
      this.handleBreakpoint();
    }, 120);
  }

  handleBreakpoint() {
    this.updateViewportMode();
    this.unload();
    // 等媒体查询样式生效后再量高 / 重建轮播
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Number(this.dataset.blockCount) > 1) this.initSlider();
        else this.syncHeight();
      });
    });
  }

  unload() {
    if (this.flickity && typeof this.flickity.destroy === "function") {
      this.flickity.destroy();
      this.flickity = null;
    }
    this.resetInlineHeights();
  }

  resetInlineHeights() {
    const root = this.closest(".announcement-bar") || this;
    root.style.height = "";
    root.style.minHeight = "";

    this.style.height = "";
    this.querySelectorAll(
      ".announcement-slider-shell, .announcement-slider--text-region, .flickity-viewport, .flickity-slider, .announcement-slider__slide, .announcement-slide__content, .announcement-text"
    ).forEach((el) => {
      el.style.height = "";
      el.style.minHeight = "";
    });
  }

  getVisibleContent(cell) {
    if (!cell) return null;
    return cell.querySelector(
      this._isMobile ? ".announcement-mobile-text" : ".announcement-pc-text"
    );
  }

  measureContentHeight(el) {
    if (!el) return 0;
    // scrollHeight 不受父级被撑高影响，更能反映内容固有高度
    return Math.ceil(Math.max(el.scrollHeight, el.offsetHeight, el.getBoundingClientRect().height));
  }

  /**
   * 量单张 slide 所需高度：可见文案高度 + slide 上下 padding。
   * 只量 .announcement-slide__content 会漏掉移动端 padding: 5px 0，
   * 再锁死 flickity-viewport / region 高度时，padding 会被 overflow:hidden 裁掉。
   */
  measureSlideHeight(cell) {
    if (!cell) return 0;

    const visible = this.getVisibleContent(cell);
    const contentHeight = this.measureContentHeight(visible || cell);
    if (!visible) return contentHeight;

    const cs = getComputedStyle(cell);
    const padY =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    return Math.ceil(contentHeight + padY);
  }

  syncHeight() {
    if (this.flickity) {
      this.syncFlickityHeight();
      return;
    }

    // 单条公告：交给 CSS 自然撑开（含移动端 slide padding），
    // 避免 PC→移动端切换后 JS 锁死高度把 padding 裁在模块外
    this.resetInlineHeights();
    if (typeof setAnnouncementBarHeight === "function") {
      setAnnouncementBarHeight();
    }
  }

  syncFlickityHeight() {
    if (!this.flickity) return;

    const viewport = this.flickity.viewport;
    if (!viewport) return;

    // 先清掉旧高度，避免 height:100% 子元素按旧高度自我拉伸
    viewport.style.height = "auto";
    this.flickity.slider && (this.flickity.slider.style.height = "");
    (this.flickity.getCellElements() || []).forEach((cell) => {
      cell.style.height = "";
    });

    let maxHeight = 0;
    (this.flickity.getCellElements() || []).forEach((cell) => {
      maxHeight = Math.max(maxHeight, this.measureSlideHeight(cell));
    });

    // 至少保证 PC 最小可读高度，但不继承错误的高大值
    if (!this._isMobile) maxHeight = Math.max(maxHeight, 50);

    if (maxHeight > 0) {
      viewport.style.height = `${maxHeight}px`;
      this.flickity.resize();
      // resize 可能再次写高度，强制写回正确值
      viewport.style.height = `${maxHeight}px`;
    }

    if (typeof setAnnouncementBarHeight === "function") {
      setAnnouncementBarHeight();
    }
  }

  initSlider() {
    const slider = this.querySelector(".announcement-slider--text-region");
    if (!slider || typeof Flickity === "undefined") return;

    this.resetInlineHeights();

    const autorotate = this.dataset.autorotate === "true";
    const speed = (parseInt(this.dataset.autorotateSpeed, 10) || 5) * 1000;

    this.flickity = new Flickity(slider, {
      accessibility: false,
      rightToLeft: !!(theme.config && theme.config.rtl),
      prevNextButtons: false,
      pageDots: false,
      wrapAround: true,
      cellAlign: "center",
      contain: false,
      setGallerySize: false,
      autoPlay: autorotate ? speed : false,
      pauseAutoPlayOnHover: true,
      draggable: true,
    });

    this.flickity.on("change", () => this.syncFlickityHeight());
    this.flickity.on("ready", () => this.syncFlickityHeight());
    requestAnimationFrame(() => this.syncFlickityHeight());
  }
}

if (!customElements.get("announcement-bar")) {
  customElements.define("announcement-bar", AnnouncementBar);
}
